import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from './api'

/**
 * A company group as the platform side sees it.
 *
 * The committed contract types `CompanyGroupResource` as flat strings
 * (`name: string`, `plan: string`, `company_count: string`). The checkpoint
 * commit 95260a8 extended the backend to serve `name` as a LocalizedString,
 * `plan` as `{id, name, is_active} | null` and `company_count` as an int, and
 * the contract was not regenerated afterwards. The screens consume what the
 * backend actually serves, so the boundary is validated here with the real
 * shape (AGENTS.md: zod at the boundary). When the contract is regenerated
 * this schema can be deleted in favour of the generated type.
 */
export const groupRowSchema = z.object({
  id: z.string(),
  name: z.record(z.string(), z.string()),
  slug: z.string().optional(),
  status: z.string(),
  plan: z
    .object({
      id: z.string(),
      name: z.record(z.string(), z.string()),
      is_active: z.boolean(),
    })
    .nullable(),
  company_count: z.union([z.number(), z.string()]),
  // Under module-based onboarding companies.max is a group override, not
  // derivable from the plan catalogue alone — the backend serves it as
  // `number | null` (Task B-4), never absent.
  companies_max: z.union([z.number(), z.string()]).nullable().optional(),
  created_at: z.string().optional(),
  // Consumed by GroupSettings (GroupSettingsGroup) and the overview header —
  // listed here too so `z.object`'s default strip behaviour does not drop
  // them from what the settings and overview screens read.
  presentation_currency: z.string().optional(),
  intercompany_mirroring: z.boolean().optional(),
  retention_days: z.number().nullable().optional(),
  purge_grace_days: z.number().nullable().optional(),
})

export type GroupRow = z.infer<typeof groupRowSchema>

/**
 * The platform's group list. Shared by the groups, plans and support screens —
 * all three need to name a group, and a feature never imports from another
 * feature (AGENTS.md), so the query lives here rather than in any one of them.
 */
export const platformGroupsQuery = queryOptions({
  queryKey: ['platform', 'groups'],
  queryFn: async (): Promise<GroupRow[]> => {
    const { data } = await api.GET('/platform/groups')
    if (!data) {
      throw new Error('GET /platform/groups returned no body')
    }
    return z.array(groupRowSchema).parse(data.data)
  },
})
